import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, LogIn } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_20601db6-8b59-495a-899a-ce4625f47158/artifacts/u4f6xq9k_image.png";
const LOGO_URL_SCROLLED = "https://customer-assets.emergentagent.com/job_construtop-db-sync/artifacts/o8vsnuau_imagen_2026-07-09_190719800-removebg-preview.png";

const navLinks = [
  { label: "Inicio", path: "/" },
  { label: "Soluciones", path: "/soluciones" },
  { label: "Sistemas", path: "/sistemas" },
  { label: "Fichas Técnicas", path: "/fichas-tecnicas" },
  { label: "Proyectos", path: "/proyectos" },
  { label: "Novedades", path: "/instagram" },
  { label: "Distribuidores", path: "/distribuidores" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const isScrolledState = scrolled || !isHome;
  const navBg = isScrolledState
    ? "bg-white/95 glass-nav border-b border-zinc-200 shadow-sm"
    : "bg-black/40 glass-nav border-b border-white/10";
  const textColor = isScrolledState ? "text-zinc-800" : "text-white";
  const activeLogo = isScrolledState ? LOGO_URL_SCROLLED : LOGO_URL;

  return (
    <nav data-testid="main-navbar" className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${navBg}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-8 flex items-center justify-between h-16 lg:h-20">
        <Link to="/" className="flex items-center gap-3" data-testid="navbar-logo">
          <img src={activeLogo} alt="Construtop" className="h-8 lg:h-10 transition-all" />
        </Link>

        <div className="hidden lg:flex items-center gap-1">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              data-testid={`nav-link-${link.path.replace("/", "") || "home"}`}
              className={`px-3 py-2 text-sm font-medium rounded-md transition-colors hover:bg-white/10 ${
                location.pathname === link.path ? "text-emerald-500 font-bold" : textColor
              }`}
            >
              {link.label}
            </Link>
          ))}
        </div>

        <div className="hidden lg:flex items-center gap-3">
          <Link to="/admin/login" data-testid="nav-login-link" className={`px-3 py-2 text-sm font-medium rounded-md transition-colors hover:bg-white/10 flex items-center gap-1.5 ${textColor}`}>
            <LogIn size={15} /> Iniciar sesión
          </Link>
          <Link to="/contacto">
            <Button data-testid="nav-contact-btn" className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold tracking-wide rounded-none px-6">
              CONTACTAR
            </Button>
          </Link>
        </div>

        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild className="lg:hidden">
            <button data-testid="mobile-menu-btn" className={`p-2 ${textColor}`}>
              {open ? <X size={24} /> : <Menu size={24} />}
            </button>
          </SheetTrigger>
          <SheetContent side="right" className="w-80 bg-zinc-950 border-zinc-800 p-0">
            <div className="flex flex-col h-full">
              <div className="p-6 border-b border-zinc-800">
                <img src={LOGO_URL} alt="Construtop" className="h-8" />
              </div>
              <div className="flex flex-col p-4 gap-1">
                {navLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    onClick={() => setOpen(false)}
                    className={`px-4 py-3 text-base font-medium rounded-md transition-colors ${
                      location.pathname === link.path ? "text-emerald-500 bg-zinc-900" : "text-zinc-300 hover:text-white hover:bg-zinc-900"
                    }`}
                  >
                    {link.label}
                  </Link>
                ))}
              </div>
              <div className="mt-auto p-6 space-y-3">
                <Link to="/admin/login" onClick={() => setOpen(false)} className="flex items-center gap-2 text-zinc-400 hover:text-white text-sm transition-colors px-4 py-2">
                  <LogIn size={16} /> Iniciar sesión
                </Link>
                <Link to="/contacto" onClick={() => setOpen(false)}>
                  <Button className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-none">
                    CONTACTAR
                  </Button>
                </Link>
              </div>
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </nav>
  );
}
